import { FocusTrap } from 'focus-trap-react';
import { h } from 'preact';
import { createPortal } from 'preact/compat';
import { useMemo, useRef } from 'preact/hooks';
import { VOID_FN } from '../../../../../utils';
import { useNewStore, useStyles } from '../../../../common/hooks';
import { isKeyBackward, isKeyForward } from '../../../../common/utils/focusTrap';
import { DIALOG_LAYER_OPEN } from '../../../theme/constants';
import { MODAL_CONTAINER_STYLES } from './styles';

export function Modal({
  show,
  styles,
  onEscapeKeyDown = VOID_FN,
  closeComponent,
  closingButtonStyle,
  attachedTo,
  children,
  ...props
}) {
  const setStyles = useStyles();

  const modalBodyRef = useRef(null);
  const { currentBreakpoint, containerHeight, containerWidth } = useNewStore('ui');

  const handleEscapeKeyDown = (e) => {
    if (e.keyCode === 27 /* ESC */) {
      e.preventDefault();
      e.stopPropagation();
      onEscapeKeyDown(e);
    }
  };

  const portalRoot = useMemo(() => {
    if (typeof document === 'undefined') return null; // SSR safety
    // We create a Portal only for modal that needs to be fullscreen, otherwise they are rendered in their current parent
    return ['xs', 'sm'].includes(currentBreakpoint) ? document.getElementById('ftv-magneto-modal-portal-container') : null;
  }, [currentBreakpoint]);

  const styleMaxHeight = useMemo(
    () => setStyles({
      default: { maxHeight: '100%' },
      'large+extraLarge': {
        maxHeight: containerHeight > 820 ? 'unset' : containerHeight * 0.80 // 820 threshold for scroll
      },
      medium: {
        maxHeight: containerHeight > containerWidth ? 'unset' : containerHeight * 0.73
      }
    }),
    [containerHeight, containerWidth]
  );

  const content = (
    <FocusTrap
      active={show}
      focusTrapOptions={{
        clickOutsideDeactivates: true,
        returnFocusOnDeactivate: false,
        isKeyForward,
        isKeyBackward
      }}
    >
      {// eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
        <div
          className="ftv-magneto--glass ftv-magneto--glass-blur-heavy ftv-magneto--glass--bordered"
          aria-modal="true"
          ref={modalBodyRef}
          onKeyDown={(e) => {
            handleEscapeKeyDown(e);
          }}
          style={{
            ...setStyles(MODAL_CONTAINER_STYLES),
            ...setStyles(styles),
            overflow: 'hidden',
            zIndex: DIALOG_LAYER_OPEN,
            ...styleMaxHeight
          }}
          role="dialog"
          {...props}
        >
          {/* Using a role here cause VoiceOver issues */}
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
          <div
            style={{ display: 'flex', width: '100%' }}
            onClick={(e) => e.stopPropagation()}
            onKeyPress={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            <div style={closingButtonStyle}>{closeComponent}</div>
            {children}
          </div>
        </div>
      }
    </FocusTrap>
  );

  return portalRoot
    ? createPortal(content, portalRoot)
    : content;
}
